import { useEffect, useMemo, useRef, useState } from 'react'
import type { CatalogDocumentDTO } from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { cn } from '@/shared/utils/cn'
import {
  filterReferenceDocuments,
  referenceSubtitle,
  referenceTitle,
} from '@/features/reference-library/referenceUtils'
import { useReferenceSelection } from './ReferenceSelectionProvider'

interface Props {
  open: boolean
  onClose: () => void
  /** Optional anchor — clicks on this element are not treated as outside. */
  anchorRef?: React.RefObject<HTMLElement | null>
}

/**
 * Popover-style multi-select picker for the Reference Library. Lives next to
 * the Scope Selector in the composer footer. Selection state is owned by
 * ``ReferenceSelectionProvider`` so the badge and the chat sender share
 * the same source of truth.
 */
export function ReferencePicker({ open, onClose, anchorRef }: Props) {
  const {
    documents,
    status,
    error,
    selectedIds,
    reload,
    toggle,
    clear,
    isDocumentAvailable,
  } = useReferenceSelection()
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => inputRef.current?.focus(), 30)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const root = containerRef.current
      if (!root) return
      const target = e.target as Node
      if (root.contains(target)) return
      if (anchorRef?.current?.contains(target)) return
      onClose()
    }
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', keyHandler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', keyHandler)
    }
  }, [open, onClose, anchorRef])

  const filtered = useMemo(
    () => filterReferenceDocuments(documents, query),
    [documents, query],
  )

  if (!open) return null

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label="Selecionar referências para esta consulta"
      data-testid="reference-picker"
      className="absolute bottom-[calc(100%+10px)] left-0 z-30 w-[min(560px,calc(100vw-32px))] rounded-2xl border border-white/10 bg-[rgba(15,16,22,0.96)] shadow-[0_30px_80px_-30px_rgba(124,92,255,0.55)] backdrop-blur-xl"
    >
      <header className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-3">
        <div className="flex items-center gap-2 text-[12.5px] font-medium text-text">
          <Icon name="book" size={14} className="text-violet" />
          Referências
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid h-7 w-7 place-items-center rounded-lg text-text-mute hover:bg-white/[0.06] hover:text-text"
          title="Fechar"
          aria-label="Fechar"
        >
          <Icon name="close" size={14} />
        </button>
      </header>

      <div className="px-4 py-3">
        <p className="m-0 text-[11.5px] leading-[1.55] text-text-mute">
          Selecione documentos de referência para complementar esta consulta.
        </p>
        <p className="m-0 mt-1.5 text-[10.5px] leading-[1.5] text-text-dim">
          As referências complementam o documento/caso selecionado. Elas ainda
          não substituem o escopo principal.
        </p>

        <div className="mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.035] px-3 py-2">
          <Icon name="search" size={13} className="text-text-mute" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault()
            }}
            placeholder="Buscar por título, tipo, domínio…"
            className="w-full bg-transparent text-[12.5px] text-text placeholder:text-text-dim focus:outline-none"
            aria-label="Buscar referência"
          />
        </div>
      </div>

      <div className="max-h-[320px] overflow-y-auto px-2 pb-2">
        {status === 'loading' && (
          <div className="px-3 py-6 text-center text-[12px] text-text-mute">
            Carregando referências…
          </div>
        )}

        {status === 'error' && (
          <div className="mx-2 my-2 rounded-lg border border-red-400/30 bg-red-500/[0.07] px-3 py-2 text-[12px] text-red-200">
            {error ?? 'Falha ao carregar a Biblioteca de Referência.'}{' '}
            <button
              type="button"
              onClick={() => void reload()}
              className="ml-1 underline hover:text-red-100"
            >
              tentar novamente
            </button>
          </div>
        )}

        {status === 'ready' && documents.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-text-mute">
            Nenhum documento na Biblioteca de Referência ainda. Acesse{' '}
            <b className="font-medium text-text">Referências</b> na barra
            lateral para adicionar.
          </div>
        )}

        {status === 'ready' && documents.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-text-mute">
            Nenhuma referência corresponde à busca.
          </div>
        )}

        {filtered.map((doc) => (
          <ReferenceRow
            key={doc.document_id}
            doc={doc}
            selected={selectedIds.includes(doc.document_id)}
            available={isDocumentAvailable(doc)}
            onToggle={() => toggle(doc.document_id)}
          />
        ))}
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-white/[0.06] px-4 py-2 text-[10.5px] uppercase tracking-[0.12em] text-text-mute">
        <span>
          {selectedIds.length} selecionada
          {selectedIds.length === 1 ? '' : 's'} · {documents.length} disponível
          {documents.length === 1 ? '' : 'is'}
        </span>
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={() => {
              clear()
            }}
            className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] font-medium uppercase tracking-[0.12em] text-text-mute transition-colors hover:border-white/20 hover:text-text"
          >
            Limpar
          </button>
        )}
      </footer>
    </div>
  )
}

interface RowProps {
  doc: CatalogDocumentDTO
  selected: boolean
  available: boolean
  onToggle: () => void
}

function ReferenceRow({ doc, selected, available, onToggle }: RowProps) {
  const title = referenceTitle(doc)
  const subtitle = referenceSubtitle(doc)
  const disabled = !available
  return (
    <button
      type="button"
      data-testid="reference-row"
      data-document-id={doc.document_id}
      onClick={() => {
        if (disabled) return
        onToggle()
      }}
      disabled={disabled}
      title={
        disabled
          ? 'Referência ainda não pronta para uso (sem chunks indexados).'
          : selected
            ? `Remover ${title} desta consulta`
            : `Anexar ${title} à consulta`
      }
      aria-pressed={selected}
      className={cn(
        'mt-1 flex w-full items-start gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-[background-color,border-color] duration-150 ease-smooth',
        !disabled && 'hover:border-white/10 hover:bg-white/[0.04]',
        selected && 'border-violet/40 bg-violet/[0.08]',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span
        className={cn(
          'grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg border',
          selected
            ? 'border-violet/40 bg-violet/[0.18] text-violet'
            : 'border-white/10 bg-white/[0.04] text-text-mute',
        )}
        aria-hidden="true"
      >
        <Icon name={selected ? 'check' : 'book'} size={14} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block truncate text-[12.5px] font-medium text-text">
          {title}
        </span>
        {subtitle && (
          <span className="block truncate text-[11px] text-text-mute">
            {subtitle}
          </span>
        )}
        <span className="block truncate font-mono text-[10.5px] text-text-dim">
          {doc.document_id}
        </span>
      </span>
      <span className="flex flex-col items-end gap-1">
        <span className="rounded-md border border-white/[0.08] bg-white/[0.03] px-1.5 py-0.5 font-mono text-[10px] text-text-mute">
          {doc.chunk_count} chunk{doc.chunk_count === 1 ? '' : 's'}
        </span>
        {selected && (
          <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-violet">
            Anexada
          </span>
        )}
      </span>
    </button>
  )
}
